import React from 'react';
import { PropTypes } from 'prop-types';
import Icon from '../../Icons/Icon';
import { capitalizeFirstLetter } from '../../../utils/api';

const TextArea = React.forwardRef(
  (
    {
      label,
      name,
      value = undefined,
      setValue = () => {},
      placeholder = undefined,
      isRequired = false,
      errorMessage = undefined,
      validationError = undefined,
      isValid = true,
      className = undefined,
      style = undefined,
      borderColor = 'border-vamos-blue-gray',
      isLabelBeside = false,
      customErrorComponent = null,
      customLabelClasses = undefined,
      customInputClasses = undefined,
      spaceY = undefined,
      fontSize = undefined,
      labelBesideLabelClasses = undefined,
      labelBesideInputClasses = undefined,
      inputTextPadding = undefined,
      customWidth = '',
      rows = 4,
      isDisabled = false,
      errorMessageClass = undefined,
      errorMessageFontClasses = undefined,
      asteriskWidth = undefined,
      isAlertIcon = false,
      customIcon = null,
      shouldNotCapitalize = false,
      ...props
    },
    ref,
  ) => {
    const onChange = (e) => {
      setValue(e.target?.value || e.currentTarget?.value);
    };

    const borderClass = `${isValid ? borderColor : ' border-vamos-red'}`;

    const backgroundColor = `${isDisabled ? 'bg-vamos-blue-gray bg-opacity-60' : 'bg-vamos-white'}`;

    const placeholderClasses = `placeholder-vamos-gray ${isDisabled && 'placeholder-opacity-60'}`;

    const labelClasses = customLabelClasses || `${isLabelBeside ? `${labelBesideLabelClasses || 'w-1/3 font-bold text-base'} ${!isRequired && 'mb-0 '}` : 'mb-0 text-sm font-medium'} ${isDisabled ? 'text-vamos-blue-gray' : 'text-vamos-black'} font-sans`;

    const inputClasses = customInputClasses || `border-solid border ${borderClass} rounded-md ${backgroundColor} focus:border-vamos-black outline-none ${inputTextPadding || 'px-3'} focus:ring-0 font-normal text-vamos-black ${placeholderClasses} ${className || ''} ${customWidth || 'w-full'} ${fontSize || 'text-base'}`;

    const icon = customIcon || <Icon name="required_field" className={`ml-1 ${asteriskWidth || 'w-3'}`} />;

    const isErrored = (isRequired && errorMessage && value === '') || !!validationError;

    const renderError = () => {
      if (customErrorComponent) {
        return customErrorComponent;
      }

      return (
        <h4 type="invalid" className={`${errorMessageClass} ${errorMessageFontClasses || 'text-vamos-red font-normal text-sm'}`}>
          {isRequired && value === '' && (
            <div className="flex">
              {isAlertIcon && <Icon name="alert" />} {errorMessage}
            </div>
          )}
          {validationError && validationError}
        </h4>
      );
    };

    return (
      <div
        {...props}
        className={`${isLabelBeside
          ? `flex ${!isRequired ? 'items-center' : ''} ${fontSize || 'text-base'} font-medium`
          : `${spaceY || 'space-y-2'} ${customWidth || 'w-full'} ${fontSize || 'text-base font-medium'}`}`}
      >
        {label && (
          <label
            className={`flex ${(isRequired && isLabelBeside) ? 'items-center h-9' : 'items-center'} ${labelClasses}`}
            htmlFor={name}
          >
            {shouldNotCapitalize ? label : capitalizeFirstLetter(label)}
            {isRequired && icon}
          </label>
        )}
        <div className={`flex flex-col ${isLabelBeside ? labelBesideInputClasses || 'w-2/3' : 'w-full'}`}>
          <textarea
            aria-required={isRequired}
            aria-invalid={isErrored}
            aria-label={label || name}
            className={`${isRequired && value === '' && 'border-vamos-red'} ${inputClasses}`}
            name={name}
            value={value}
            onChange={onChange}
            placeholder={placeholder}
            id={name}
            rows={rows}
            ref={ref}
            disabled={isDisabled}
            style={style}
          />
          {isErrored && (
          <div>
            {renderError()}
          </div>
          )}
        </div>
      </div>
    );
  },
);

TextArea.displayName = 'TextArea';

TextArea.propTypes = {
  label: PropTypes.string,
  name: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
  ]),
  setValue: PropTypes.func,
  placeholder: PropTypes.string,
  isRequired: PropTypes.bool,
  errorMessage: PropTypes.string,
  validationError: PropTypes.string,
  isValid: PropTypes.bool,
  className: PropTypes.string,
  style: PropTypes.object,
  borderColor: PropTypes.string,
  isLabelBeside: PropTypes.bool,
  customErrorComponent: PropTypes.node,
  customLabelClasses: PropTypes.string,
  customInputClasses: PropTypes.string,
  spaceY: PropTypes.string,
  fontSize: PropTypes.string,
  labelBesideLabelClasses: PropTypes.string,
  labelBesideInputClasses: PropTypes.string,
  inputTextPadding: PropTypes.string,
  customWidth: PropTypes.string,
  rows: PropTypes.number,
  isDisabled: PropTypes.bool,
  errorMessageClass: PropTypes.string,
  errorMessageFontClasses: PropTypes.string,
  asteriskWidth: PropTypes.string,
  isAlertIcon: PropTypes.bool,
  customIcon: PropTypes.node,
  shouldNotCapitalize: PropTypes.bool,
};

export default TextArea;
